import React, { useMemo } from "react";
import {
  Box,
  Text,
  Flex,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Progress,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import ChapterVideoItem from "./ChapterVideoItem";
import useMediaQ from "@root/hooks/useMediaQ";
import { formatDuration, getChapterSummary } from "@root/utils/date-helper";

interface ChaptersListProps {
  chapters: any[];
  selectedChapter: number;
  enrolled: boolean;
  courseProgressData: any;
  onChapterSelect: (chapterIdx: number, firstVideo: any) => void;
  onVideoSelect: (video: any, chapterIdx: number) => void;
}

const ChaptersList: React.FC<ChaptersListProps> = ({
  chapters,
  selectedChapter,
  enrolled,
  courseProgressData,
  onChapterSelect,
  onVideoSelect,
}) => {
  const { t } = useTranslation("common");
  const { sm } = useMediaQ();
  const isMobile = !sm;
  console.log("chapters", chapters);

  // Create a map of video progress by video ID
  const videoProgressMap = useMemo(() => {
    const map = new Map<number, number>();
    const progressArray = Array.isArray(courseProgressData?.[0])
      ? courseProgressData[0]
      : Array.isArray(courseProgressData)
      ? courseProgressData
      : [];

    progressArray.forEach((item: any) => {
      const videoId = item?.video?.id;
      if (videoId !== null && videoId !== undefined) {
        if (item?.completed === true) {
          map.set(videoId, 100);
        } else {
          const progress = typeof item?.progress === "number"
            ? item.progress
            : parseFloat(item?.progress || "0");
          if (!Number.isNaN(progress)) {
            map.set(videoId, Math.max(0, Math.min(100, progress)));
          }
        }
      }
    });
    return map;
  }, [courseProgressData]);

  // Calculate chapter progress
  const getChapterProgress = (chapter: any) => {
    const videos = chapter?.courseVideos || [];
    if (videos.length === 0) return 0;

    let totalProgressSum = 0;
    videos.forEach((video: any) => {
      if (video?.id) {
        const videoProgress = videoProgressMap.get(video.id) || 0;
        totalProgressSum += videoProgress;
      }
    });

    return Math.round(totalProgressSum / videos.length);
  };

  const renderChaptersVideo = (chapter: any, idx: number) => {
    return (
      <Box>
        {chapter?.courseVideos?.map((video: any, videoIdx: number) => (
          <ChapterVideoItem
            key={video?.id || videoIdx}
            video={video}
            videoIdx={videoIdx}
            enrolled={enrolled}
            courseProgressData={courseProgressData}
            onVideoSelect={onVideoSelect}
            chapterIdx={idx}
          />
        ))}
      </Box>
    );
  };

  return (
    <Box data-tour-item="journalist-cd-chapters">
      <Text fontWeight={600} fontSize="20px" color={"#2F2F34"} mb={4}>
        {t("courseDetail.chapters")}
      </Text>
      <Box
        bg={isMobile ? "transparent" : "#CDD6DC33"}
        borderRadius="lg"
        borderWidth={isMobile ? 0 : 1}
        borderColor="#CDD6DC"
        overflow="hidden"
      >
        <Accordion allowToggle>
          {chapters?.map((chapter: any, idx: number) => {
            const isActive = selectedChapter === idx;
            const totalDuration =
              chapter?.courseVideos?.reduce(
                (acc: number, video: any) => acc + (video?.duration || 0),
                0,
              ) || 0;

            return (
              <AccordionItem
                key={chapter?.title || chapter?.id}
                borderBottom={isMobile ? "none" : "1px solid"}
                borderBottomColor="#CDD6DC"
                mb={isMobile ? 3 : 0}
              >
                <AccordionButton
                  height={isMobile ? "40px" : "auto"}
                  px={isMobile ? 4 : 6}
                  py={isMobile ? 4 : 4}
                  bg={isMobile ? (isActive ? "#EBF4FD" : "white") : "#CDD6DC33"}
                  borderBottomWidth={isMobile ? 0 : 1}
                  borderBottomColor="gray.100"
                  borderRadius={isMobile ? "12px" : "none"}
                  border={
                    isMobile
                      ? isActive
                        ? "1px solid #0085D8"
                        : "1px solid #E0E0E0"
                      : "none"
                  }
                  _hover={{
                    bg: isMobile
                      ? isActive
                        ? "#EBF4FD"
                        : "#F5F5F5"
                      : "#CDD6DC33",
                  }}
                  onClick={() => {
                    if (isMobile) {
                      onChapterSelect(idx, chapter?.courseVideos?.[0]);
                    }
                  }}
                >
                  <Flex flex={1} align="center" justify="space-between">
                    <Flex align="center" flex={1} gap={3}>
                      {isMobile && (
                        <Box
                          w={5}
                          h={5}
                          borderRadius="full"
                          bg={isActive ? "#0085D8" : "#9E9E9E"}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          color="white"
                          fontWeight="600"
                          fontSize="14px"
                        >
                          {idx + 1}
                        </Box>
                      )}
                      {!isMobile && <AccordionIcon mr={3} color="#5C6670" />}
                      <Text
                        fontSize="16px"
                        fontWeight={600}
                        color={
                          isMobile
                            ? isActive
                              ? "#0085D8"
                              : "#2F2F34"
                            : "#2F2F34"
                        }
                      >
                        {chapter?.title}
                      </Text>
                    </Flex>
                    <Flex align="center" gap={2}>
                      {isMobile && (
                        <Flex
                          align="center"
                          gap={2}
                          color="#5C6670"
                          fontSize="14px"
                        >
                          <Text>⏱️</Text>
                          <Text>{formatDuration(totalDuration)}</Text>
                        </Flex>
                      )}
                      {!isMobile && (
                        <>
                          <Text fontSize="14px" color="#5C6670">
                            {getChapterSummary(chapter)}
                          </Text>
                          {enrolled && (
                            <Flex align="center" gap={2} minW="120px">
                              <Text fontSize="12px" color="#5C6670">
                                {getChapterProgress(chapter)}%
                              </Text>
                              <Progress
                                value={getChapterProgress(chapter)}
                                height="8px"
                                width="80px"
                                colorScheme="blue"
                                borderRadius="40px"
                              />
                            </Flex>
                          )}
                        </>
                      )}
                    </Flex>
                  </Flex>
                </AccordionButton>
                <AccordionPanel px={0} py={0} bg="white">
                  {enrolled && isMobile && (
                    <Box px={6} py={2} borderBottom="1px solid" borderColor="#E0E0E0">
                      <Flex align="center" gap={2}>
                        <Text fontSize="12px" color="#5C6670" minW="35px">
                          {getChapterProgress(chapter)}%
                        </Text>
                        <Progress
                          value={getChapterProgress(chapter)}
                          height="8px"
                          flex={1}
                          colorScheme="blue"
                          borderRadius="40px"
                        />
                      </Flex>
                    </Box>
                  )}
                  {renderChaptersVideo(chapter, idx)}
                </AccordionPanel>
              </AccordionItem>
            );
          })}
        </Accordion>
      </Box>
    </Box>
  );
};

export default ChaptersList;
